import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useRenewalForecast } from '@/services/dashboardService';
import { formatCalendarDate, formatCurrency } from '@/utils/formatters';
import { downloadCsv } from '@/utils/csv';
import { RenewalForecastGrouping, RENEWAL_FORECAST_MONTHS, isRenewalForecastMonths } from '@/types';
import type { RenewalForecastMonths } from '@/types';
import { GroupByToggle } from '@/components/dashboard/GroupByToggle';
import { RenewalForecastChart } from '@/components/dashboard/RenewalForecastChart';
import { RenewalForecastTable } from '@/components/dashboard/RenewalForecastTable';
import { rowsOfForecast, rowsOfPeriod } from '@/components/dashboard/forecastRows';
import type { RenewalForecastRow } from '@/components/dashboard/forecastRows';

const DEFAULT_MONTHS: RenewalForecastMonths = 12;

/** The calendar day before an exclusive UTC-midnight bound, as yyyy-MM-dd. */
function dayBefore(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) - 1));
  return date.toISOString().slice(0, 10);
}

const CSV_HEADERS = [
  'Contract Number',
  'Title',
  'Vendor',
  'Date',
  'Date Source',
  'Period',
  'Amount',
  'Currency',
  'Auto-Renews',
  'Owner',
  'Department',
  'Status',
];

function csvRow(row: RenewalForecastRow) {
  return [
    row.contractNumber,
    row.title,
    row.vendorName,
    row.date.slice(0, 10),
    row.dateSource,
    row.periodLabel,
    row.amount,
    row.currency,
    row.autoRenews,
    row.ownerName ?? '',
    row.departmentName ?? '',
    row.statusName,
  ];
}

/**
 * Renewals page (#18): the full forecast. Grouping and window length live in the URL
 * (`/renewals?groupBy=month|quarter&months=6|12|24|36`), so a link from the dashboard, Back and a
 * pasted URL agree; the selected period is local and resets when the query changes. The CSV holds
 * the contracts currently listed: the selected period's, or every one in the window.
 */
const Renewals: React.FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: '/renewals' });
  const groupBy = search.groupBy ?? RenewalForecastGrouping.Month;
  const months = search.months ?? DEFAULT_MONTHS;
  const queryId = `${groupBy}:${months}`;

  const [selection, setSelection] = React.useState<{ queryId: string; key: string | null }>({ queryId, key: null });
  // A new grouping or window clears the selection for good (adjusting state during render, not in
  // an effect), so returning to the earlier parameters does not bring an old selection back
  if (selection.queryId !== queryId) {
    setSelection({ queryId, key: null });
  }
  const selectedKey = selection.queryId === queryId ? selection.key : null;

  const { data: forecast, isLoading, isPlaceholderData, error } = useRenewalForecast(months, groupBy);
  // While a new grouping or window loads, the previous result is still on screen: no export or selection then
  const isStale = isPlaceholderData;
  const selectedPeriod = selectedKey ? forecast?.periods.find((p) => p.key === selectedKey) ?? null : null;
  const listedRows: RenewalForecastRow[] = forecast
    ? selectedPeriod
      ? rowsOfPeriod(selectedPeriod)
      : rowsOfForecast(forecast)
    : [];

  const setGroupBy = (value: RenewalForecastGrouping) => {
    navigate({ to: '/renewals', search: (prev) => ({ ...prev, groupBy: value }) });
  };

  const setMonths = (value: string) => {
    const parsed = Number(value);
    if (!isRenewalForecastMonths(parsed)) return;
    navigate({ to: '/renewals', search: (prev) => ({ ...prev, months: parsed }) });
  };

  const handleExport = () => {
    if (listedRows.length === 0 || isStale) return;
    const stamp = format(new Date(), 'yyyy-MM-dd'); // local date, not UTC
    downloadCsv(`renewal-forecast-${groupBy}-${stamp}.csv`, CSV_HEADERS, listedRows.map(csvRow));
  };

  const windowCaption = forecast
    ? `${formatCalendarDate(forecast.windowStart)} to ${formatCalendarDate(dayBefore(forecast.windowEnd))}`
    : '';

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-grey-800">Renewals</h1>
          <p className="text-sm text-grey-500 mt-1">
            Contracts renewing or expiring by {groupBy === 'quarter' ? 'fiscal quarter' : 'month'}
            {windowCaption ? `, ${windowCaption}` : ''}
            {forecast ? `: ${forecast.contractCount} contracts, ${formatCurrency(forecast.totalAmount)}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <GroupByToggle value={groupBy} onChange={setGroupBy} />
          <Select value={String(months)} onValueChange={setMonths}>
            <SelectTrigger className="w-[140px]" aria-label="Months ahead" data-testid="renewals-months">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RENEWAL_FORECAST_MONTHS.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option} months
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={listedRows.length === 0 || isStale}
            data-testid="renewals-export"
          >
            <Download className="h-4 w-4 mr-2" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>Failed to load the renewal forecast: {error.message}</AlertDescription>
        </Alert>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Projected amounts by period</CardTitle>
          <CardDescription>Auto-renewing contracts below, contracts needing a decision on top. Click a bar to list its contracts.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-[320px] w-full" />
          ) : forecast && forecast.contractCount > 0 ? (
            <div className={cn(isStale && 'opacity-60 pointer-events-none')} aria-busy={isStale}>
              <RenewalForecastChart
                periods={forecast.periods}
                selectedKey={selectedKey}
                onSelect={(key) => setSelection({ queryId, key })}
                height={320}
              />
            </div>
          ) : error ? null : (
            <div className="flex items-center justify-center h-[320px]">
              <p className="text-sm text-grey-500">No renewals in this window</p>
            </div>
          )}
        </CardContent>
      </Card>

      {forecast && !isStale && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Periods</CardTitle>
            <CardDescription>Select a row to list that period's contracts</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto contain-inline-size">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Contracts</TableHead>
                    <TableHead className="text-right">Auto-renews</TableHead>
                    <TableHead className="text-right">Decision needed</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {forecast.periods.map((period) => {
                    const selected = period.key === selectedKey;
                    return (
                      <TableRow
                        key={period.key}
                        role="row"
                        aria-selected={selected}
                        tabIndex={0}
                        className={cn('cursor-pointer', selected && 'bg-primary/10 hover:bg-primary/10')}
                        onClick={() => setSelection({ queryId, key: selected ? null : period.key })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelection({ queryId, key: selected ? null : period.key });
                          }
                        }}
                      >
                        <TableCell className="font-medium whitespace-nowrap">{period.label}</TableCell>
                        <TableCell className="text-right tabular-nums">{period.contractCount}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(period.autoRenewAmount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(period.decisionNeededAmount)}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{formatCurrency(period.totalAmount)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{selectedPeriod ? selectedPeriod.label : 'All contracts in the window'}</CardTitle>
          <CardDescription>
            {listedRows.length} {listedRows.length === 1 ? 'contract' : 'contracts'}
            {selectedPeriod ? (
              <>
                {' '}
                <button type="button" className="text-primary hover:underline" onClick={() => setSelection({ queryId, key: null })}>
                  Show all periods
                </button>
              </>
            ) : null}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading || isStale ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-[48px] w-full" />
              <Skeleton className="h-[48px] w-full" />
            </div>
          ) : error ? null : (
            <RenewalForecastTable rows={listedRows} showPeriod emptyMessage="No contracts in this window" />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Renewals;
